/**
 * La conversazione con l'assistente: messaggi, suggerimenti e casella per
 * scrivere.
 *
 * Sta sia nel pannello (V58) sia nella vista a tutta pagina (V59), e legge e
 * scrive l'unica conversazione in memoria (`contesto.ts`), cosi passare
 * dall'uno all'altra non la interrompe. I numeri delle risposte arrivano
 * dalle funzioni: quelle chiamate, con i dati che l'assistente ha letto, si
 * vedono sotto la risposta quando si chiede di mostrarle, e l'assistente non scrive nulla, indica le
 * viste in cui farlo.
 */

import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { VistaAperta } from '@calcio/shared';
import { puo } from '@calcio/shared';
import {
  ArrowUpIcon,
  CalculatorIcon,
  ChevronRightIcon,
  DatabaseIcon,
  MessageCircleQuestionIcon,
  OctagonXIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupTextarea,
} from '@/components/ui/input-group';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { chiama } from '../api/client.js';
import { useContesto } from '../navigazione/contesto.js';
import { type EsitoMessaggio, type MessaggioConversazione, useAssistente } from './contesto.js';
import { suggerimenti } from './suggerimenti.js';
import { avvisoNumeri, intestazioneLettura } from './testo.js';
import { TestoRisposta } from './TestoRisposta.js';

export interface ContestoDomanda {
  squadraStagioneId?: string;
  vista?: VistaAperta;
}

export function Dialogo({
  contesto,
  domande,
  onVai,
}: {
  /** Squadra-stagione e vista che partono con la domanda. */
  contesto: ContestoDomanda;
  domande: { usate: number; massimo: number };
  /** Prima di seguire un collegamento: nel pannello lo chiude. */
  onVai?: () => void;
}) {
  const { utente } = useContesto();
  const { conversazione, imposta, aggiornaStato, mostraLetture } = useAssistente();
  const [testo, setTesto] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const fondo = useRef<HTMLDivElement>(null);

  const messaggi = conversazione.messaggi;
  const leggeVoti = puo(utente.assegnazioni, 'voto.leggi', contesto.squadraStagioneId);
  const leggeAttributi = puo(utente.assegnazioni, 'misurazione.leggi', contesto.squadraStagioneId);

  // Ogni risposta porta la conversazione in fondo, dove si sta leggendo.
  useEffect(() => {
    fondo.current?.scrollIntoView({ block: 'end' });
  }, [messaggi]);

  const invia = async (domanda: string) => {
    if (!domanda.trim() || inCorso) return;
    setInCorso(true);
    setErrore(null);
    setTesto('');
    // La domanda compare subito: l'attesa della risposta è di secondi.
    const provvisorio: MessaggioConversazione = {
      id: `in-corso-${Date.now()}`,
      ruolo: 'user',
      testo: domanda,
      creatoIl: new Date().toISOString(),
    };
    imposta({ ...conversazione, messaggi: [...messaggi, provvisorio] });

    try {
      const esito = await chiama<EsitoMessaggio>('/assistente/messaggi', {
        metodo: 'POST',
        corpo: {
          testo: domanda,
          ...(conversazione.id && { conversazioneId: conversazione.id }),
          ...contesto,
        },
      });
      imposta({
        ...conversazione,
        id: esito.conversazioneId,
        messaggi: [...messaggi, ...esito.messaggi],
      });
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Risposta non arrivata.');
    } finally {
      setInCorso(false);
      // La domanda conta nel limite del mese anche se la risposta non arriva.
      void aggiornaStato();
    }
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messaggi.length === 0 ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              Leggo i dati della squadra e rispondo con i numeri che trovo. Non creo e non modifico
              nulla: ti dico dove farlo.
            </p>
            <p className="text-[13px] text-muted-foreground">Per cominciare</p>
            <ul className="flex flex-col gap-2">
              {suggerimenti(contesto.vista?.chiave, { leggeVoti, leggeAttributi }).map((s) => (
                <li key={s}>
                  <Button
                    variant="outline"
                    className="h-auto w-full justify-start py-2 text-left whitespace-normal"
                    onClick={() => void invia(s)}
                  >
                    <MessageCircleQuestionIcon data-icon="inline-start" />
                    {s}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <ol className="flex flex-col gap-3">
            {messaggi
              .filter((m) => mostraLetture || m.ruolo !== 'tool')
              .map((m) => (
                <Messaggio key={m.id} messaggio={m} onVai={onVai} />
              ))}
          </ol>
        )}

        {inCorso && (
          <p className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
            <Spinner />
            Sto leggendo i dati…
          </p>
        )}
        {errore && (
          <Alert variant="destructive" className="mt-3">
            <OctagonXIcon />
            <AlertTitle>Risposta non arrivata</AlertTitle>
            <AlertDescription>{errore}</AlertDescription>
          </Alert>
        )}
        <div ref={fondo} />
      </div>

      <form
        className="border-t border-border p-3"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void invia(testo);
        }}
      >
        <InputGroup>
          <InputGroupTextarea
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            onKeyDown={(e) => {
              // Invio manda, Maiusc+Invio va a capo: si scrive di corsa.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void invia(testo);
              }
            }}
            placeholder="Scrivi una domanda…"
            aria-label="Domanda"
            rows={2}
          />
          <InputGroupAddon align="block-end">
            <InputGroupText className="text-xs tabular-nums">
              {domande.usate} di {domande.massimo} domande questo mese
            </InputGroupText>
            <InputGroupButton
              type="submit"
              variant="default"
              size="icon-sm"
              className="ml-auto rounded-full"
              aria-label="Invia"
              disabled={inCorso || !testo.trim()}
            >
              <ArrowUpIcon />
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>
      </form>
    </>
  );
}

/** "esecuzione_test" → "Esecuzione test": il nome della funzione, leggibile. */
function Messaggio({
  messaggio,
  onVai,
}: {
  messaggio: MessaggioConversazione;
  onVai?: () => void;
}) {
  const navigate = useNavigate();

  if (messaggio.ruolo === 'tool') {
    const errore = (messaggio.dati as { errore?: string } | null)?.errore;
    const { nome, sintesi, calcolo } = intestazioneLettura(messaggio.funzione, messaggio.dati);
    const Icona = errore ? TriangleAlertIcon : calcolo ? CalculatorIcon : DatabaseIcon;
    return (
      <li className="text-xs">
        <Collapsible className="group/lettura rounded-md border border-border bg-card">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-auto w-full justify-start gap-2 px-2 py-1.5 text-xs font-normal text-muted-foreground"
            >
              <ChevronRightIcon className="transition-transform group-data-[state=open]/lettura:rotate-90" />
              <Icona className={cn(errore && 'text-destructive')} />
              <span className="font-medium text-foreground">{nome}</span>
              <span className="truncate">{sintesi}</span>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <pre className="max-h-64 overflow-auto border-t border-border p-2 text-[11px] whitespace-pre-wrap">
              {JSON.stringify(messaggio.dati, null, 2)}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      </li>
    );
  }

  // Il testo di una risposta con sole letture è vuoto: non si mostra una
  // riga vuota, i risultati parlano da soli.
  if (!messaggio.testo) return null;

  const mio = messaggio.ruolo === 'user';
  return (
    <li
      className={cn(
        'max-w-[85%] rounded-lg px-3 py-2 text-sm',
        mio
          ? 'self-end rounded-br-sm bg-primary whitespace-pre-wrap text-primary-foreground'
          : 'self-start rounded-bl-sm border border-border bg-card',
      )}
    >
      {mio ? (
        messaggio.testo
      ) : (
        <>
          <TestoRisposta
            testo={messaggio.testo}
            onVai={(percorso) => {
              onVai?.();
              void navigate(percorso);
            }}
          />
          {messaggio.numeriDaVerificare?.length ? (
            <Alert className="mt-2">
              <TriangleAlertIcon />
              <AlertTitle>Numeri da verificare</AlertTitle>
              <AlertDescription>{avvisoNumeri(messaggio.numeriDaVerificare)}</AlertDescription>
            </Alert>
          ) : null}
        </>
      )}
    </li>
  );
}
